import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { firebaseConfigured } from "@/lib/firebase";
import FirebaseAuthPanel from "@/components/FirebaseAuthPanel";
import { BrandLogo, BrandWordmark } from "@/components/Brand";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState(null); // "firebase" | "emergent"

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (location.state?.error) toast.error(location.state.error);
  }, [location.state]);

  // Ask the backend which auth mode is active. Firebase is used only when configured on BOTH sides;
  // otherwise fall back to Emergent Google OAuth so the app always has a working sign-in.
  useEffect(() => {
    let active = true;
    api.get("/auth/config")
      .then((r) => { if (active) setMode(r.data?.firebase_enabled && firebaseConfigured ? "firebase" : "emergent"); })
      .catch(() => { if (active) setMode("emergent"); });
    return () => { active = false; };
  }, []);

  const handleEmergentLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left — brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-14 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1587522705669-61de54c723bf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw0fHxtaW5pbWFsJTIwd29ya3NwYWNlJTIwZGVza3xlbnwwfHx8fDE3ODY1NTgyMDB8MA&ixlib=rb-4.1.0&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-primary/75" />
        <div className="relative z-10 text-primary-foreground">
          <BrandWordmark className="text-4xl" />
        </div>
        <div className="relative z-10 text-primary-foreground">
          <h1 className="font-heading text-4xl leading-tight mb-3">The Business Operating System for solo founders.</h1>
          <p className="font-accent text-lg text-primary-foreground/90 mb-4">Your business, in one place.</p>
          <p className="text-primary-foreground/85 max-w-md leading-relaxed">
            One organized workspace for your whole business, business operations, content, SOPs, projects,
            planning, knowledge and Business Center, with AI guidance grounded in your own business,
            so you always know what to work on next.
          </p>
        </div>
      </div>

      {/* Right — sign in */}
      <div className="relative flex items-center justify-center p-8">
        <div className="lg:hidden absolute top-8 left-8">
          <BrandLogo className="h-12 w-auto max-w-[220px] object-contain object-left" />
        </div>

        {mode === null && (
          <div className="flex items-center gap-3 text-muted-foreground" data-testid="login-loading">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading…
          </div>
        )}

        {mode === "firebase" && <FirebaseAuthPanel />}

        {mode === "emergent" && (
          <div className="w-full max-w-sm animate-gentle-fade">
            <h2 className="font-heading text-3xl text-foreground mb-2">Welcome</h2>
            <p className="text-muted-foreground mb-10 leading-relaxed">
              Sign in with your Google account to open your private workspace.
            </p>
            <Button
              onClick={handleEmergentLogin}
              data-testid="google-login-button"
              className="w-full rounded-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground text-base shadow-soft"
            >
              Continue with Google
            </Button>
            <p className="text-xs text-muted-foreground mt-8 leading-relaxed">
              New here? Signing in creates your workspace automatically. Your data stays private to your account.
            </p>
          </div>
        )}

        <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-5 text-xs text-muted-foreground" data-testid="login-legal-nav">
          <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
          <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
          <a href="mailto:support@workandkeep.app" className="hover:text-foreground transition-colors">Contact</a>
        </div>
      </div>
    </div>
  );
}
